import { StyledButton } from "@aureskonnect/react-ui";
import { CATALOGUE_MODE } from "@constants";
import { t } from "i18next";

import React from "react";
import Modal from "react-modal";

type ResetFontConfirmationModalProps = {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

const overlayStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 4,
};

const contentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0px",
    color: "black",
    height: "30rem",
    width: "37rem",
    zIndex: 3,
    backgroundColor: "#075d9b",
    border: "1px solid #075d9b",
    bottom: "unset",
    top: "unset",
    right: "unset",
    left: "unset",
    zoom: process.env.REACT_APP_MODE === CATALOGUE_MODE ? "50%" : "100%",
    borderRadius: "20px",
};

const containerStyle: React.CSSProperties = {
    gap: "80px",
    background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
    padding: "3rem 2rem",
    borderRadius: "20px",
    border: "1px solid #374151",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    position: "relative",
    overflow: "hidden",
    backdropFilter: "blur(10px)",
    minWidth: "500px",
    height: "100%",
};

const accentBarStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #077dbc 0%, #065a94 50%, #077dbc 100%)",
};

const titleStyle: React.CSSProperties = {
    textAlign: "center",
    fontSize: "var(--font-size-subtitle)",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "0.5px",
    textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
    lineHeight: 1.2,
};

const messageStyle: React.CSSProperties = {
    textAlign: "center",
    fontSize: "var(--font-size-body)",
    fontWeight: 400,
    color: "#d1d5db",
    letterSpacing: "0.3px",
    lineHeight: 1.5,
    marginLeft: "60px",
    marginRight: "60px",
    opacity: 0.95,
};

const buttonsRowStyle: React.CSSProperties = {
    gap: "20px",
    marginTop: "20px",
};

const backButtonStyle: React.CSSProperties = {
    width: "179px",
    height: "61px",
    fontSize: "var(--font-size-base)",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    transition: "all 0.2s ease",
    background: "#ffffff",
    color: "#1f2937",
    letterSpacing: "0.5px",
    cursor: "pointer",
};

const confirmButtonStyle: React.CSSProperties = {
    width: "233px",
    height: "61px",
    fontSize: "var(--font-size-base)",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    borderRadius: "12px",
    border: "none",
    boxShadow: "0 10px 25px -5px rgba(7, 125, 188, 0.4)",
    transition: "all 0.2s ease",
    background: "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
    color: "#ffffff",
    letterSpacing: "0.5px",
    cursor: "pointer",
};

function ResetFontConfirmationModalComponent({
    isOpen,
    onConfirm,
    onCancel,
}: ResetFontConfirmationModalProps): JSX.Element | null {
    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            onRequestClose={onCancel}
            style={{ overlay: overlayStyle, content: contentStyle }}
        >
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={containerStyle}
            >
                <div style={accentBarStyle} />
                <div style={titleStyle}>{t("Confirmation")}</div>
                <div style={messageStyle}>
                    {t(
                        "Are you sure you want to reset font settings to defaults?"
                    )}
                </div>
                <div className="d-flex" style={buttonsRowStyle}>
                    <StyledButton
                        rounded={true}
                        className="m-2"
                        style={backButtonStyle}
                        onClick={onCancel}
                        data-testid="font-reset-back"
                    >
                        {t("Back")}
                    </StyledButton>
                    <StyledButton
                        rounded={true}
                        className="m-2"
                        style={confirmButtonStyle}
                        onClick={onConfirm}
                        data-testid="font-reset-confirm"
                    >
                        {t("Confirm")}
                    </StyledButton>
                </div>
            </div>
        </Modal>
    );
}

export const ResetFontConfirmationModal = React.memo(
    ResetFontConfirmationModalComponent
);
